import React, { useState } from 'react';
import styled from 'styled-components';
import { useQuery } from 'react-query';
import toast from 'react-hot-toast';
import { adminSeasonsAPI, adminAnalyticsAPI } from '../services/adminApi';

const Container = styled.div`
  background: white;
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  padding: 2rem;
`;

const Title = styled.h2`
  margin: 0 0 0.5rem 0;
  font-size: 1.5rem;
  font-weight: 700;
  color: #1f2937;
`;

const Subtitle = styled.p`
  margin: 0 0 1.5rem 0;
  color: #6b7280;
  font-size: 0.875rem;
  line-height: 1.5;
`;

const FormSection = styled.div`
  background: #f8fafc;
  padding: 1.5rem;
  border-radius: 8px;
  border: 1px solid #e5e7eb;
  max-width: 32rem;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 0.5rem;
  font-weight: 500;
  color: #374151;
  font-size: 0.875rem;
`;

const Select = styled.select`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 0.875rem;
  margin-bottom: 1rem;
  &:focus {
    outline: none;
    border-color: #3b82f6;
  }
`;

const ExportButton = styled.button`
  background: #043663;
  color: white;
  border: none;
  padding: 0.75rem 1.25rem;
  border-radius: 6px;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  &:hover:not(:disabled) {
    background: #2563eb;
  }
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

export default function AdminSettingsAnalytics() {
  const [seasonId, setSeasonId] = useState('');
  const [exporting, setExporting] = useState(false);

  const { data: seasonsData, isLoading } = useQuery('admin-analytics-seasons', () => adminSeasonsAPI.getAll(), {
    retry: false
  });

  const seasons = seasonsData?.data || [];

  const handleExport = async () => {
    if (!seasonId) {
      toast.error('Select a phase first.');
      return;
    }
    setExporting(true);
    try {
      const blob = await adminAnalyticsAPI.downloadDivisionTopTwoCsv(seasonId);
      if (blob && blob.type && blob.type.includes('application/json')) {
        const text = await blob.text();
        let msg = 'Export failed';
        try {
          const j = JSON.parse(text);
          msg = j.message || msg;
        } catch (_) {
          /* ignore */
        }
        throw new Error(msg);
      }
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `division-top-two-${seasonId.slice(0, 8)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success('Download started.');
    } catch (e) {
      console.error(e);
      toast.error(e.message || 'Export failed');
    } finally {
      setExporting(false);
    }
  };

  return (
    <Container>
      <Title>Analytics</Title>
      <Subtitle>
        Export a CSV of the 1st and 2nd place teams in each division leaderboard for the selected phase (same
        ordering as the public division leaderboard). Only divisions that have at least one team assignment or standing
        for that phase are included. Each row includes team name, email, phone, standings stats, and up to two active
        team members (name, email, skill, phone).
      </Subtitle>
      <FormSection>
        <Label htmlFor="analytics-season">Phase</Label>
        <Select
          id="analytics-season"
          value={seasonId}
          onChange={(e) => setSeasonId(e.target.value)}
          disabled={isLoading}
        >
          <option value="">{isLoading ? 'Loading phases…' : 'Select phase'}</option>
          {seasons.map((s) => (
            <option key={s.id} value={s.id}>
              {s.name} {s.status ? `(${s.status})` : ''}
            </option>
          ))}
        </Select>
        <ExportButton type="button" onClick={handleExport} disabled={exporting || !seasonId}>
          {exporting ? 'Preparing…' : 'Export CSV'}
        </ExportButton>
      </FormSection>
    </Container>
  );
}
